import React, {createContext, useContext, useState, ReactNode, useEffect, useCallback} from "react";
import {Client} from "../../../types/userManagement";
import {useSearchParams} from "react-router-dom";
import {useDebounce} from "use-debounce";
import {useAuth} from "../../../context/user";
import {useUserManagementBrowsing} from "../../../lib/user-management/UserManagementBrowsingContext";
import {useDepartments} from "../../../api/platform/useDepartments";
import {Department, ListDepartmentsResponse} from "../../../api/platform/departments";

interface DepartmentsManagementContextType {
    initialClientID?: string;
    client?: Client;
    setClient: (client?: Client) => void;
    departments?: ListDepartmentsResponse;
    isLoading: boolean;
    error: unknown;
    selected: Set<string>;
    addSelected: (id: string) => void;
    removeSelected: (id: string) => void;
    selectAll: () => void;
    deselectAll: () => void;
    page: number;
    setPage: (page: number) => void;
    limit: number;
    refetch: () => void;
    actionError: string;
    setActionError: (error: string) => void;
    archived: boolean;
    setArchived: (archived: boolean) => void;
    search: string;
    setSearch: (search: string) => void;
}

const DepartmentsManagementContext = createContext<DepartmentsManagementContextType | undefined>(undefined);

interface DepartmentsManagementProviderProps {
    children: ReactNode;
}

export const DepartmentsManagementProvider: React.FC<DepartmentsManagementProviderProps> = ({children}) => {
    const auth = useAuth();
    const browsing = useUserManagementBrowsing();

    const [searchParams, setSearchParams] = useSearchParams();
    const [client, setClient] = useState<Client>();
    const [selected, setSelected] = useState(new Set<string>());
    const [page, setPage] = useState<number>(Number(searchParams.get("page")) || 1);
    const [limit] = useState<number>(10);
    const [archived, setArchived] = useState(false);
    const [search, setSearch] = useState("");
    const [actionError, setActionError] = useState("");

    const [debouncedSearch] = useDebounce(search, 300);

    useEffect(() => { setPage(1); }, [debouncedSearch]);

    useEffect(() => {
        if (actionError !== "") {
            const t = setTimeout(() => setActionError(""), 10_000);
            return () => clearTimeout(t);
        }
    }, [actionError]);

    const clientID = auth.isSuperAdmin ? (client?.id ?? "") : auth.clientID;

    const {data: departments, isLoading, error, refetch} = useDepartments({
        clientID,
        page,
        limit,
        search: debouncedSearch,
        hydrate: ["userCount", "guardianCount"],
        archived,
    });

    useEffect(() => {
        browsing.setClientID(client?.id ?? "");
    }, [client]);

    useEffect(() => {
        if (client?.id) {
            const newParams = new URLSearchParams(searchParams.toString());
            newParams.set("clientID", client.id);
            newParams.set("page", `${page}`);
            newParams.set("search", debouncedSearch);
            setSearchParams(newParams);
        }
    }, [client, page, debouncedSearch]);

    const addSelected = useCallback((id: string) => {
        setSelected(prev => new Set(prev).add(id));
    }, []);

    const removeSelected = useCallback((id: string) => {
        setSelected(prev => { const s = new Set(prev); s.delete(id); return s; });
    }, []);

    const selectAll = useCallback(() => {
        if (departments) {
            setSelected(new Set(departments.items.map((d: Department) => d.id).filter(Boolean)));
        }
    }, [departments]);

    const deselectAll = useCallback(() => setSelected(new Set()), []);

    const initialClientID = searchParams.get("clientID") ?? undefined;

    return (
        <DepartmentsManagementContext.Provider value={{
            client,
            setClient,
            departments,
            isLoading,
            error,
            initialClientID,
            addSelected,
            removeSelected,
            selectAll,
            deselectAll,
            selected,
            page,
            limit,
            setPage,
            refetch,
            actionError,
            setActionError,
            archived,
            setArchived,
            search,
            setSearch,
        }}>
            {children}
        </DepartmentsManagementContext.Provider>
    );
};

export const useDepartmentsManagement = (): DepartmentsManagementContextType => {
    const context = useContext(DepartmentsManagementContext);
    if (!context) {
        throw new Error("useDepartmentsManagement must be used within a DepartmentsManagementProvider");
    }
    return context;
};
